import { useCallback, useMemo } from 'react'
import { useMutation } from '@apollo/client/react'
import { AdminAPI, GraphQL } from '@guestgoodie/graphql'
import { useLingui } from '@lingui/react/macro'

import * as Form from '@/features/form'

export type CustomerFormData = {
  email: string
  contactName: string
  companyName: string
  vat: string
  line1: string
  line2: string
  country: string
  postalCode: string
  city: string
}

export type RoomsFormData = {
  rooms: number
  interval: GraphQL.BillingInterval
}

export type FormData = CustomerFormData & RoomsFormData

export function useBillingForm({ onDone }: { onDone: () => void }) {
  const { t } = useLingui()

  const [activateBilling, { loading: loadingActivate }] = useMutation(
    AdminAPI.billing.ACTIVATE,
    {
      refetchQueries: [AdminAPI.organization.GET] // Need to update the cache
    }
  )

  const [updateBilling, { loading: loadingUpdate }] = useMutation(
    AdminAPI.billing.UPDATE,
    {
      refetchQueries: [AdminAPI.organization.GET] // Need to update the cache
    }
  )

  const { errors, handlePromiseCatch } = Form.useErrorHelpers({})

  const errorOpts = useMemo(() => {
    return {
      unknownString: t({
        comment:
          'Generic error message that we show when something unexpected happens.',
        message:
          'Something went wrong, please try again, if this keeps happening please write our support.'
      }),
      customErrors: {
        too_many_spaces: {
          key: 'error-too-many-spaces',
          message: t({
            comment:
              'Error message that is shown to the user when they try to safe their licensing with less rooms than they currently have.',
            message:
              'You selected less rooms than you currently have, delete them to proceed.'
          })
        }
      }
    }
  }, [t])

  const activate = useCallback(
    async (data: FormData) => {
      return activateBilling({
        variables: {
          input: {
            city: data.city,
            companyName: data.companyName,
            contactName: data.contactName,
            country: data.country,
            postalCode: data.postalCode,
            email: data.email,
            interval: data.interval,
            line1: data.line1,
            rooms: data.rooms,
            vat: data.vat,
            line2: data.line2
          }
        }
      })
        .then(({ data, error }) => {
          if (error) return Promise.reject(error)
          if (!data) return Promise.reject('unknown')

          onDone()
        })
        .catch(error => handlePromiseCatch(error, errorOpts))
    },
    [onDone, activateBilling, handlePromiseCatch, errorOpts]
  )

  const update = useCallback(
    async (data: FormData) => {
      return updateBilling({
        variables: {
          input: {
            city: data.city,
            companyName: data.companyName,
            contactName: data.contactName,
            country: data.country,
            postalCode: data.postalCode,
            email: data.email,
            interval: data.interval,
            line1: data.line1,
            rooms: data.rooms,
            vat: data.vat,
            line2: data.line2
          }
        }
      })
        .then(({ data, error }) => {
          if (error) return Promise.reject(error)
          if (!data) return Promise.reject('unknown')

          onDone()
        })
        .catch(error => handlePromiseCatch(error, errorOpts))
    },
    [onDone, updateBilling, handlePromiseCatch, errorOpts]
  )

  return {
    errors,
    activate,
    update,
    loading: loadingActivate || loadingUpdate
  }
}
